import { HttpParams, HttpResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { HttpService, UrlService } from '@boels/helpers';
import {
  Address,
  AddressValidation,
  BoelsBillingAddress,
  Cart,
  CartUrlEnum,
  DeliveryMode,
  DeliveryModeResponse,
} from '@boels/models';
import { filter, Observable, switchMap, withLatestFrom } from 'rxjs';
import { ApiContextService, CartManagementService } from '../../services';

@Injectable({
  providedIn: 'root',
})
export class DeliveryGateway {
  private readonly httpService = inject(HttpService);
  private urlService = inject(UrlService);
  private apiContextService = inject(ApiContextService);
  private cartManagementService = inject(CartManagementService);

  getDeliveryModeResponse(): Observable<DeliveryModeResponse> {
    return this.apiContextService.getContext().pipe(
      filter(([baseSite]) => !!baseSite),
      withLatestFrom(
        this.cartManagementService.getCartUser(),
        this.cartManagementService.getCartId(),
      ),
      switchMap(([[baseSite, language, currency], cartUser, cartId]) => {
        const params = [
          ['lang', language],
          ['curr', currency],
          ['fields', 'FULL'],
        ];

        const url = this.urlService.combineUri(
          baseSite.uid,
          this.cartManagementService.getCartUrl(
            cartUser.type,
            CartUrlEnum.CARTS,
          ),
          cartId,
          'deliverymodes',
        );
        const httpHeaders = this.cartManagementService.getHttpHeaders(cartUser);
        return this.httpService.get<DeliveryModeResponse>(
          url,
          params,
          httpHeaders,
        );
      }),
    );
  }

  setDeliveryMode(
    deliveryMode: DeliveryMode,
  ): Observable<HttpResponse<DeliveryMode>> {
    return this.apiContextService.getContext().pipe(
      filter(([baseSite]) => !!baseSite),
      withLatestFrom(
        this.cartManagementService.getCartUser(),
        this.cartManagementService.getCartId(),
      ),
      switchMap(([[baseSite, language, currency], cartUser, cartId]) => {
        const params:
          | HttpParams
          | {
              [param: string]:
                | string
                | number
                | boolean
                | ReadonlyArray<string | number | boolean>;
            } = {
          deliveryModeId: deliveryMode.code as string,
          lang: language,
          curr: currency,
          fields: 'FULL',
        };

        const url = this.urlService.combineUri(
          baseSite.uid,
          this.cartManagementService.getCartUrl(
            cartUser.type,
            CartUrlEnum.CARTS,
          ),
          cartId,
          'deliverymode',
        );

        const httpHeaders = this.cartManagementService.getHttpHeaders(cartUser);
        return this.httpService.putWithResponse(url, null, {
          params,
          headers: httpHeaders,
          observe: 'response',
        });
      }),
    );
  }

  verifyAddress(address: Address): Observable<AddressValidation> {
    return this.apiContextService.getContext().pipe(
      filter(([baseSite]) => !!baseSite),
      withLatestFrom(this.cartManagementService.getCartUser()),
      switchMap(([[baseSite, language, currency], cartUser]) => {
        const params = [
          ['lang', language],
          ['curr', currency],
        ];

        const url = this.urlService.combineUri(
          baseSite.uid,
          this.cartManagementService.getCartUrl(
            cartUser.type,
            CartUrlEnum.ADDRESS,
          ),
        );

        const httpHeaders = this.cartManagementService.getHttpHeaders(cartUser);
        return this.httpService.post<AddressValidation>(
          url,
          address,
          params,
          httpHeaders,
        );
      }),
    );
  }

  createDeliveryAddress(
    address: BoelsBillingAddress,
  ): Observable<BoelsBillingAddress> {
    return this.apiContextService.getContext().pipe(
      filter(([baseSite]) => !!baseSite),
      withLatestFrom(
        this.cartManagementService.getCartUser(),
        this.cartManagementService.getCartId(),
      ),
      switchMap(([[baseSite, language, currency], cartUser, cartId]) => {
        const params = [
          ['lang', language],
          ['curr', currency],
        ];
        const url = this.urlService.combineUri(
          baseSite.uid,
          this.cartManagementService.getCartUrl(
            cartUser.type,
            CartUrlEnum.CARTS,
          ),
          cartId,
          'addresses/delivery',
        );

        const httpHeaders = this.cartManagementService.getHttpHeaders(cartUser);
        return this.httpService.post<BoelsBillingAddress>(
          url,
          address,
          params,
          httpHeaders,
        );
      }),
    );
  }

  setDeliveryAddress(addressId: string): Observable<Address> {
    return this.apiContextService.getContext().pipe(
      filter(([baseSite]) => !!baseSite),
      withLatestFrom(
        this.cartManagementService.getCartUser(),
        this.cartManagementService.getCartId(),
      ),
      switchMap(([[baseSite, language, currency], cartUser, cartId]) => {
        const params = [
          ['lang', language],
          ['curr', currency],
          ['addressId', addressId],
        ];

        const url = this.urlService.combineUri(
          baseSite.uid,
          this.cartManagementService.getCartUrl(
            cartUser.type,
            CartUrlEnum.CARTS,
          ),
          cartId,
          'addresses/delivery',
        );

        const httpHeaders = this.cartManagementService.getHttpHeaders(cartUser);
        return this.httpService.put<Address>(url, {}, params, httpHeaders);
      }),
    );
  }

  setOperationalDirections(
    directions?: string,
    method: 'DELETE' | 'PUT' = 'PUT',
  ): Observable<Cart> {
    return this.apiContextService.getContext().pipe(
      filter(([baseSite]) => !!baseSite),
      withLatestFrom(
        this.cartManagementService.getCartUser(),
        this.cartManagementService.getCartId(),
      ),
      switchMap(([[baseSite, language, currency], cartUser, cartId]) => {
        const params: string[][] = [
          ['lang', language],
          ['curr', currency],
        ];

        if (method === 'PUT') {
          params.push(['fields', 'FULL']);
        }

        const url = this.urlService.combineUri(
          baseSite.uid,
          this.cartManagementService.getCartUrl(
            cartUser.type,
            CartUrlEnum.CARTS,
          ),
          cartId,
          'optdirs',
        );
        const httpHeaders = this.cartManagementService.getHttpHeaders(cartUser);

        const httpMethodMap = {
          DELETE: this.httpService.delete<Cart>(
            url,
            params,
            httpHeaders,
            'application/json;charset=UTF-8',
          ),
          PUT: this.httpService.put<Cart>(
            url,
            { directions },
            params,
            httpHeaders,
          ),
        };

        return httpMethodMap[method];
      }),
    );
  }
}
